import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';

import { Platform, View } from 'react-native';

import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { AppLogo } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    LOADING_SPINNER_ID,
    LOADING_TEXT_ID,
    LOGO_TEST_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { setInitialWebPathName } from 'store/config/actions';
import {
    selectInitialWebPathParams,
    selectLayoutConfig,
} from 'store/config/selectors';
import { selectIfCollectionSuspensionActive } from 'store/user/selectors';

import { RegularStorage, StorageKeys } from 'services/mmkv';

import getScreenParamsFromPathName from 'utils/getScreenParamsFromPathName';
import { isTablet, isTabletOrWeb } from 'utils/responsiveness';

import LocalizedText from 'components/LocalizedText';
import LoadingCircle from 'components/Spinner';

import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PERSONALIZE_SCREEN
    >;
    route: RouteProp<RouteNavigationParams, SCREEN.PERSONALIZE_SCREEN>;
};

const {
    LOGIN: { FTI_WELCOME_SCREEN_LOADING },
} = LANGUAGE_KEYS;
const screenMinShowTime = 1500; // ms

const PersonalizeScreen = ({ navigation, route }: Props) => {
    const dispatch = useDispatch();
    const [loadingTextVisible, setLoadingTextVisible] = useState<boolean>(true);

    const logoSize = Platform.select({
        web: 200,
        default: isTablet ? 160 : 120,
    });

    const timer = useMemo(() => {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, screenMinShowTime);
        });
    }, []);

    const initialWebPathParams = useSelector(selectInitialWebPathParams);

    const { background } = useSelector(selectLayoutConfig);
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );

    const handleInitialRouteNavigation = useCallback(() => {
        if (initialWebPathParams.pathName) {
            const { screen, params } =
                getScreenParamsFromPathName(initialWebPathParams);

            navigation.reset({
                routes: [
                    {
                        name: SCREEN.ROOT,
                        state: {
                            routes: [{ name: screen, params }],
                        },
                    },
                ],
            });

            dispatch(
                setInitialWebPathName({
                    pathName: '',
                    query: undefined,
                }),
            );
        } else {
            navigation.replace(SCREEN.ROOT);
        }
    }, [initialWebPathParams]);

    useLayoutEffect(() => {
        (async () => {
            const personalizePageShown = await RegularStorage.getItem(
                StorageKeys.PERSONALIZE_PAGE_SHOWN,
            );

            setLoadingTextVisible(!personalizePageShown);
        })();
    }, []);

    useEffect(() => {
        timer.then(() => {
            handleInitialRouteNavigation();

            if (loadingTextVisible) {
                RegularStorage.setItem(
                    StorageKeys.PERSONALIZE_PAGE_SHOWN,
                    JSON.stringify(true),
                );
            }
        });
    }, []);

    return (
        <View
            style={[
                gStyles.container,
                gStyles.centerItems,
                { backgroundColor: background },
            ]}
        >
            <View style={gStyles.flex} />
            <View>
                <View style={styles.topLogo}>
                    <AppLogo
                        testID={LOGO_TEST_ID}
                        width={logoSize}
                        height={logoSize}
                    />
                </View>
                <LoadingCircle
                    testID={LOADING_SPINNER_ID}
                    size={isTabletOrWeb ? 48 : 32}
                    routeKey={route.key}
                />
            </View>
            <View style={gStyles.flex} />

            {(loadingTextVisible || collectionSuspensionActive) && (
                <LocalizedText
                    testID={LOADING_TEXT_ID}
                    style={[styles.textStyle, styles.textMargin]}
                    textKey={FTI_WELCOME_SCREEN_LOADING}
                />
            )}
        </View>
    );
};

export default PersonalizeScreen;
